// Executive Content Studio — Website Content Manager
// ===================================================
// Admin workspace for editing every piece of website copy that lives in the
// `site_content_keys` / `site_content_versions` tables. Grouped by page,
// with per-item edit, publish, revision history and restore. Read-back
// verification is enforced by the DB helpers `publish_content_version` and
// `restore_content_version` (both admin-only, SECURITY DEFINER).
import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { Search, History, Save, Send, Loader2, FileText, Undo2, ExternalLink, CheckCircle2 } from "lucide-react";

// ---- Types ----------------------------------------------------------------
interface KeyRow {
  id: string;
  key: string;
  page: string;
  section: string | null;
  label: string;
  description: string | null;
  fallback_text: string;
  variable_schema: Array<{ name: string; label?: string }>;
  is_shared: boolean;
  content_type: "plain" | "markdown" | "html";
  max_length: number | null;
  updated_at: string;
}
interface VersionRow {
  id: string;
  key_id: string;
  value: string;
  status: "draft" | "published" | "archived";
  reason: string | null;
  parent_version_id: string | null;
  created_by: string | null;
  created_at: string;
  published_at: string | null;
  published_by: string | null;
}

// ---- Data hooks -----------------------------------------------------------
function useKeys() {
  const [rows, setRows] = useState<KeyRow[] | null>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("site_content_keys" as never)
        .select("*")
        .order("page")
        .order("section")
        .order("label");
      if (!alive) return;
      if (error) {
        toast({ title: "Couldn't load content", description: error.message, variant: "destructive" });
        setRows([]);
        return;
      }
      setRows((data ?? []) as unknown as KeyRow[]);
    })();
    return () => { alive = false; };
  }, []);
  return { rows, reload: async () => {
    const { data } = await supabase.from("site_content_keys" as never).select("*").order("page").order("section").order("label");
    setRows((data ?? []) as unknown as KeyRow[]);
  }};
}

function usePublishedValues() {
  const [map, setMap] = useState<Record<string, { value: string; published_at: string | null; version_id: string | null }>>({});
  const reload = async () => {
    const { data } = await supabase.from("site_content_current" as never).select("key_id,value,published_at,version_id");
    const m: typeof map = {};
    ((data ?? []) as Array<{ key_id: string; value: string | null; published_at: string | null; version_id: string | null }>).forEach((r) => {
      m[r.key_id] = { value: r.value ?? "", published_at: r.published_at, version_id: r.version_id };
    });
    setMap(m);
  };
  useEffect(() => { void reload(); }, []);
  return { map, reload };
}

// ---- Page -----------------------------------------------------------------
export default function ContentStudioPage() {
  const { rows: keys } = useKeys();
  const { map: published, reload: reloadPublished } = usePublishedValues();
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [historyKeyId, setHistoryKeyId] = useState<string | null>(null);

  // Group by page → items
  const grouped = useMemo(() => {
    const map = new Map<string, KeyRow[]>();
    (keys ?? []).forEach((k) => {
      if (!map.has(k.page)) map.set(k.page, []);
      map.get(k.page)!.push(k);
    });
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [keys]);

  const filtered = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    return grouped
      .map<[string, KeyRow[]]>(([page, items]) => [
        page,
        items.filter((k) => {
          const val = published[k.id]?.value ?? k.fallback_text;
          return (
            k.key.toLowerCase().includes(q) ||
            k.label.toLowerCase().includes(q) ||
            (k.section ?? "").toLowerCase().includes(q) ||
            val.toLowerCase().includes(q)
          );
        }),
      ])
      .filter(([, items]) => items.length > 0);
  }, [grouped, search, published]);

  const selected = (keys ?? []).find((k) => k.id === selectedKeyId) ?? null;
  const publishedRecent = useMemo(() => {
    return Object.entries(published)
      .filter(([, v]) => v.published_at)
      .sort((a, b) => (b[1].published_at ?? "").localeCompare(a[1].published_at ?? ""))
      .slice(0, 5)
      .map(([keyId, v]) => ({ key: (keys ?? []).find((k) => k.id === keyId), when: v.published_at }))
      .filter((x) => x.key);
  }, [published, keys]);

  return (
    <AdminShell>
      <div className="space-y-4">
        <div>
          <h1 className="font-heading text-2xl">Content Studio</h1>
          <p className="text-sm text-muted-foreground">Edit website words without touching code.</p>
        </div>
        {/* Executive Experience */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" /> What's happening with website copy
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Editable items</div>
              <div className="text-2xl font-heading">{keys?.length ?? "…"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Pages covered</div>
              <div className="text-2xl font-heading">{grouped.length}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Recently published</div>
              {publishedRecent.length === 0 ? (
                <div className="text-muted-foreground text-xs">Nothing published yet.</div>
              ) : (
                <ul className="text-xs space-y-0.5">
                  {publishedRecent.map((r) => (
                    <li key={r.key!.id} className="truncate">
                      <span className="font-medium">{r.key!.label}</span>{" "}
                      <span className="text-muted-foreground">
                        · {formatDistanceToNow(new Date(r.when!), { addSuffix: true })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-[minmax(280px,340px)_1fr] gap-4">
          {/* Left: page tree + search */}
          <Card className="h-fit">
            <CardHeader className="pb-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search wording…"
                  className="pl-7 h-9"
                />
              </div>
            </CardHeader>
            <CardContent className="p-2">
              <ScrollArea className="h-[65vh]">
                {keys === null ? (
                  <div className="p-4 text-sm text-muted-foreground flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">No content matches your search.</div>
                ) : (
                  filtered.map(([page, items]) => (
                    <div key={page} className="mb-3">
                      <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                        {page}
                      </div>
                      <ul className="space-y-0.5">
                        {items.map((k) => {
                          const isSel = selectedKeyId === k.id;
                          const preview = published[k.id]?.value ?? k.fallback_text;
                          return (
                            <li key={k.id}>
                              <button
                                type="button"
                                onClick={() => setSelectedKeyId(k.id)}
                                className={`w-full text-left px-2 py-1.5 rounded text-sm transition ${
                                  isSel ? "bg-primary/10 border border-primary/30" : "hover:bg-muted"
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate flex-1">{k.label}</span>
                                  {k.section && (
                                    <Badge variant="outline" className="text-[9px] shrink-0">{k.section}</Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">{preview}</div>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Right: editor */}
          <div>
            {selected ? (
              <ContentEditor
                key={selected.id}
                item={selected}
                currentValue={published[selected.id]?.value ?? selected.fallback_text}
                onSaved={reloadPublished}
                onOpenHistory={() => setHistoryKeyId(selected.id)}
              />
            ) : (
              <Card>
                <CardContent className="py-16 text-center text-muted-foreground text-sm">
                  Choose a content item on the left to edit it.
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <HistoryDialog
        keyId={historyKeyId}
        keyRow={(keys ?? []).find((k) => k.id === historyKeyId) ?? null}
        onClose={() => setHistoryKeyId(null)}
        onRestored={reloadPublished}
      />
    </AdminShell>
  );
}

// ---- Editor ---------------------------------------------------------------
function ContentEditor({
  item,
  currentValue,
  onSaved,
  onOpenHistory,
}: {
  item: KeyRow;
  currentValue: string;
  onSaved: () => void | Promise<void>;
  onOpenHistory: () => void;
}) {
  const [draft, setDraft] = useState(currentValue);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<null | "save" | "publish">(null);
  const [verified, setVerified] = useState<string | null>(null);
  useEffect(() => {
    setDraft(currentValue);
    setVerified(null);
  }, [currentValue, item.id]);

  const dirty = draft !== currentValue;
  const overLimit = item.max_length !== null && draft.length > item.max_length;

  const saveDraft = async () => {
    setBusy("save");
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from("site_content_versions" as never).insert({
      key_id: item.id,
      value: draft,
      status: "draft",
      reason: reason || null,
      created_by: user?.id ?? null,
    } as never);
    setBusy(null);
    if (error) {
      toast({ title: "Couldn't save draft", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Draft saved" });
  };

  const publish = async () => {
    if (overLimit) {
      toast({ title: "Too long", description: `Please shorten to ${item.max_length} characters or fewer.`, variant: "destructive" });
      return;
    }
    setBusy("publish");
    setVerified(null);
    const { data: { user } } = await supabase.auth.getUser();
    // 1) Create a new version row.
    const { data: inserted, error: insErr } = await supabase
      .from("site_content_versions" as never)
      .insert({ key_id: item.id, value: draft, status: "draft", reason: reason || null, created_by: user?.id ?? null } as never)
      .select("id")
      .single();
    if (insErr || !inserted) {
      setBusy(null);
      toast({ title: "Couldn't create version", description: insErr?.message ?? "unknown", variant: "destructive" });
      return;
    }
    // 2) Publish it via the SECURITY DEFINER helper.
    const versionId = (inserted as { id: string }).id;
    const { error: pubErr } = await supabase.rpc("publish_content_version" as never, {
      _version_id: versionId,
      _reason: reason || null,
    } as never);
    if (pubErr) {
      setBusy(null);
      toast({ title: "Publish failed", description: pubErr.message, variant: "destructive" });
      return;
    }
    // 3) Read-back verification — hit the public view and check the served value.
    const { data: verifyRows } = await supabase
      .from("site_content_current" as never)
      .select("value")
      .eq("key_id", item.id)
      .limit(1);
    const served = (verifyRows?.[0] as { value: string | null } | undefined)?.value ?? null;
    setBusy(null);
    if (served !== draft) {
      toast({
        title: "Couldn't verify the page changed",
        description: `The database returned "${served}" instead of your new wording. The version stays pending.`,
        variant: "destructive",
      });
      return;
    }
    setVerified(draft);
    setReason("");
    await onSaved();
    toast({ title: "Published & verified", description: "The live site now serves this wording." });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="text-lg">{item.label}</CardTitle>
            <div className="text-xs text-muted-foreground mt-1">
              <span className="font-mono">{item.key}</span>
              <span className="mx-2">·</span>
              {item.page}
              {item.section ? <> · {item.section}</> : null}
            </div>
            {item.description && (
              <p className="text-xs text-muted-foreground mt-2 max-w-prose">{item.description}</p>
            )}
          </div>
          <div className="flex flex-col gap-2 items-end">
            <Button variant="outline" size="sm" onClick={onOpenHistory}>
              <History className="h-3.5 w-3.5 mr-1.5" /> History
            </Button>
            <a
              href={`/${item.page.toLowerCase().replace(/\s+/g, "-")}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              Open live page <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Wording</label>
            <div className={`text-xs ${overLimit ? "text-destructive" : "text-muted-foreground"}`}>
              {draft.length}
              {item.max_length ? ` / ${item.max_length}` : ""} chars
            </div>
          </div>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(3, Math.min(10, Math.ceil(draft.length / 60)))}
            className="font-sans"
          />
        </div>

        {item.variable_schema?.length > 0 && (
          <div className="rounded border border-dashed p-3 bg-muted/40">
            <div className="text-xs font-semibold mb-1.5">Variables you can use</div>
            <div className="flex flex-wrap gap-1.5">
              {item.variable_schema.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => setDraft((d) => d + `{{${v.name}}}`)}
                  className="text-[11px] font-mono px-2 py-0.5 rounded bg-background border hover:border-primary"
                  title={v.label ?? v.name}
                >
                  {`{{${v.name}}}`}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Reason for change (optional)
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="What are you changing and why?"
            className="mt-1"
          />
        </div>

        {/* Preview */}
        <div className="rounded border p-4 bg-background/50">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Preview</div>
          <div className="text-sm whitespace-pre-wrap">{draft || <span className="text-muted-foreground italic">(empty)</span>}</div>
        </div>

        {verified && (
          <div className="flex items-center gap-2 rounded border border-primary/30 bg-primary/5 text-primary text-sm p-2.5">
            <CheckCircle2 className="h-4 w-4" />
            Verified — the live site now serves this wording.
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <div className="text-xs text-muted-foreground">
            Currently live: <span className="font-medium text-foreground">{currentValue.slice(0, 60)}{currentValue.length > 60 ? "…" : ""}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={saveDraft} disabled={busy !== null || !dirty}>
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save draft
            </Button>
            <Button onClick={publish} disabled={busy !== null || !dirty || overLimit}>
              {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
              Publish
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---- History dialog -------------------------------------------------------
function HistoryDialog({
  keyId,
  keyRow,
  onClose,
  onRestored,
}: {
  keyId: string | null;
  keyRow: KeyRow | null;
  onClose: () => void;
  onRestored: () => void | Promise<void>;
}) {
  const [rows, setRows] = useState<VersionRow[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  useEffect(() => {
    if (!keyId) { setRows(null); return; }
    (async () => {
      const { data } = await supabase
        .from("site_content_versions" as never)
        .select("*")
        .eq("key_id", keyId)
        .order("created_at", { ascending: false })
        .limit(50);
      setRows((data ?? []) as unknown as VersionRow[]);
    })();
  }, [keyId]);

  const restore = async (v: VersionRow) => {
    setBusyId(v.id);
    const { error } = await supabase.rpc("restore_content_version" as never, {
      _source_version_id: v.id,
      _reason: "Restored from history",
    } as never);
    setBusyId(null);
    if (error) {
      toast({ title: "Restore failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Restored & published" });
    await onRestored();
    onClose();
  };

  return (
    <Dialog open={!!keyId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Revision history</DialogTitle>
          <DialogDescription>{keyRow?.label} · <span className="font-mono">{keyRow?.key}</span></DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] pr-3">
          {rows === null ? (
            <div className="text-sm text-muted-foreground p-4">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground p-4">No history yet.</div>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => (
                <li key={r.id} className="rounded border p-3">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant={r.status === "published" ? "default" : r.status === "draft" ? "secondary" : "outline"}>
                        {r.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    {r.status !== "published" && (
                      <Button size="sm" variant="outline" disabled={busyId === r.id} onClick={() => restore(r)}>
                        {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Undo2 className="h-3.5 w-3.5 mr-1" /> Restore</>}
                      </Button>
                    )}
                  </div>
                  {r.reason && <div className="text-xs text-muted-foreground mb-1">{r.reason}</div>}
                  <div className="text-sm whitespace-pre-wrap font-mono bg-muted/40 p-2 rounded">{r.value}</div>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
